import { useState } from "react";
import { Phone } from "lucide-react";
import axios from "axios";
import Reveal from "../Reveal";
import WhatsAppIcon from "../WhatsAppIcon";
import { CONTACTS } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PACKAGES_LIST = ["On Your Own Tour", "Guided Group Tour", "Bespoke Private Tour", "Not sure yet"];

const initialForm = {
    name: "",
    phone: "",
    email: "",
    destination: "",
    travellers: "",
    dates: "",
    package: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState("idle");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API}/enquiries`, form);
            setStatus("done");
        } catch {
            setStatus("error");
        }
    };

    return (
        <section id="contact" className="section-pad" data-testid="contact-section">
            <div className="container-luxe grid gap-14 lg:grid-cols-2 lg:gap-16">
                <div>
                    <Reveal>
                        <p className="eyebrow mb-5">Contact</p>
                        <h2 className="font-serif text-navy text-3xl sm:text-4xl leading-snug">
                            Begin the <span className="italic text-gold">conversation</span>
                        </h2>
                        <p className="mt-5 max-w-md font-light leading-relaxed">
                            Tell us where you&rsquo;re dreaming of. We&rsquo;ll call back with real experiences, honest
                            advice and a journey drawn around you — the first conversation is always free.
                        </p>
                    </Reveal>

                    <div className="mt-10 space-y-5">
                        {CONTACTS.map((c, i) => (
                            <Reveal key={c.name} delay={0.1 * i}>
                                <div className="bg-white border border-line p-6 flex flex-col sm:flex-row sm:items-center gap-5" data-testid={`contact-card-${c.name.toLowerCase()}`}>
                                    <span className="flex items-center justify-center h-12 w-12 rounded-full border border-gold/60 font-serif italic text-gold text-xl shrink-0">
                                        {c.initial}
                                    </span>
                                    <div className="flex-1">
                                        <p className="font-serif text-navy text-lg leading-tight">{c.fullName}</p>
                                        <a
                                            data-testid={`contact-tel-${c.name.toLowerCase()}`}
                                            href={`tel:${c.tel}`}
                                            className="text-sm text-stone hover:text-gold transition-colors duration-300 mt-0.5 inline-block"
                                        >
                                            {c.phoneDisplay}
                                        </a>
                                    </div>
                                    <div className="flex gap-3">
                                        <a
                                            data-testid={`contact-wa-${c.name.toLowerCase()}`}
                                            href={c.whatsapp}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 bg-forest text-cream px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#274d3d]"
                                        >
                                            <WhatsAppIcon size={14} /> WhatsApp
                                        </a>
                                        <a
                                            data-testid={`contact-call-${c.name.toLowerCase()}`}
                                            href={`tel:${c.tel}`}
                                            className="inline-flex items-center gap-2 border border-navy/25 text-navy px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase transition-all duration-300 hover:border-gold hover:text-gold hover:-translate-y-0.5"
                                        >
                                            <Phone size={13} /> Call
                                        </a>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal delay={0.15}>
                    {status === "done" ? (
                        <div className="h-full flex flex-col items-center justify-center bg-white border border-gold/50 p-10 text-center" data-testid="enquiry-success">
                            <span className="flex items-center justify-center h-14 w-14 rounded-full border border-gold text-gold font-serif italic text-2xl">✓</span>
                            <h3 className="mt-6 font-serif text-navy text-2xl">Thank you, {form.name.split(" ")[0]}.</h3>
                            <p className="mt-3 text-sm font-light leading-relaxed max-w-sm">
                                Your enquiry is with us. We&rsquo;ll reach out shortly — and if you&rsquo;d like to talk
                                right now, WhatsApp us directly.
                            </p>
                            <a
                                data-testid="enquiry-success-whatsapp"
                                href={CONTACTS[0].whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-7 inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 text-[11px] tracking-[0.14em] uppercase hover:-translate-y-0.5 transition-all duration-300"
                            >
                                <WhatsAppIcon size={14} /> WhatsApp Dnyanada
                            </a>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="bg-white border border-line p-7 sm:p-9" data-testid="enquiry-form">
                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
                                <div>
                                    <label htmlFor="f-name" className="field-label">Name *</label>
                                    <input id="f-name" data-testid="enquiry-name" required value={form.name} onChange={set("name")} className="field" placeholder="Your name" />
                                </div>
                                <div>
                                    <label htmlFor="f-phone" className="field-label">Phone *</label>
                                    <input id="f-phone" data-testid="enquiry-phone" required type="tel" value={form.phone} onChange={set("phone")} className="field" placeholder="+91 …" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="f-email" className="field-label">Email *</label>
                                    <input id="f-email" data-testid="enquiry-email" required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@example.com" />
                                </div>
                                <div>
                                    <label htmlFor="f-destination" className="field-label">Destination</label>
                                    <input id="f-destination" data-testid="enquiry-destination" value={form.destination} onChange={set("destination")} className="field" placeholder="Where to?" />
                                </div>
                                <div>
                                    <label htmlFor="f-travellers" className="field-label">Travellers</label>
                                    <input id="f-travellers" data-testid="enquiry-travellers" value={form.travellers} onChange={set("travellers")} className="field" placeholder="e.g. 2 adults, 1 child" />
                                </div>
                                <div>
                                    <label htmlFor="f-dates" className="field-label">Approximate dates</label>
                                    <input id="f-dates" data-testid="enquiry-dates" value={form.dates} onChange={set("dates")} className="field" placeholder="e.g. mid-December" />
                                </div>
                                <div>
                                    <label htmlFor="f-package" className="field-label">Package of interest</label>
                                    <select id="f-package" data-testid="enquiry-package" value={form.package} onChange={set("package")} className="field cursor-pointer">
                                        <option value="">Choose…</option>
                                        {PACKAGES_LIST.map((p) => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="f-message" className="field-label">Message</label>
                                    <textarea id="f-message" data-testid="enquiry-message" rows="3" value={form.message} onChange={set("message")} className="field resize-none" placeholder="Tell us how you like to travel…" />
                                </div>
                            </div>

                            <button
                                type="submit"
                                data-testid="enquiry-submit"
                                disabled={status === "sending"}
                                className="btn-gold w-full mt-8 disabled:opacity-60 disabled:translate-y-0"
                            >
                                {status === "sending" ? "Sending…" : "Send Enquiry"}
                            </button>
                            {status === "error" && (
                                <p className="mt-4 text-sm text-[#a04b3c] text-center" data-testid="enquiry-error">
                                    Something went wrong — please WhatsApp us instead.
                                </p>
                            )}
                            <p className="mt-4 text-[11px] text-stone/60 text-center tracking-wide">
                                No spam, no newsletters — just a reply from a real person.
                            </p>
                        </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
}
